import React, { useState, useEffect, useCallback } from "react";
import api, { fmtVND, fmtFull } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  RefreshCw, Loader2,
  Wifi, WifiOff, TrendingUp, Calendar, Zap, Trash2, Bot, BarChart3, Store,
} from "lucide-react";

import AutoCrawlTab from "@/components/kiotviet/AutoCrawlTab";

let ApexChart = null;
try { ApexChart = require("react-apexcharts").default; } catch (e) {}

const SUB_TABS = [
  { key: "dashboard", label: "Tổng quan", icon: BarChart3 },
  { key: "auto_crawl", label: "Tự động Crawl", icon: Bot },
  { key: "webhooks", label: "Webhook", icon: Wifi },
];

export default function KiotVietPage() {
  const [subTab, setSubTab] = useState("dashboard");
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => { api.get("/kiotviet/status").then(r => setStatus(r.data)).catch(() => {}); }, []);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/kiotviet/sync/all");
      toast.success(data.message);
      setStatus(prev => prev ? { ...prev, counts: data.results } : prev);
      api.get("/kiotviet/status").then(r => setStatus(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi sync"); }
    finally { setSyncing(false); }
  };

  const handleClearSync = async () => {
    const pin = window.prompt("Nhập mã PIN xác nhận (4 số):");
    if (pin !== "6789") { if (pin !== null) toast.error("PIN không đúng"); return; }
    setClearing(true);
    try {
      const { data } = await api.post("/kiotviet/clear-sync", { pin: "6789" });
      toast.success(data.message);
      api.get("/kiotviet/status").then(r => setStatus(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setClearing(false); }
  };


  return (
    <div className="space-y-4" data-testid="kiotviet-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0162e8] to-[#4680ff] flex items-center justify-center shadow-lg">
            <Store className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#F8FAFC]">KiotViet</h1>
            <div className="flex items-center gap-2 text-[10px]">
              {status?.connected ? (
                <span className="text-emerald-400 flex items-center gap-1"><Wifi className="w-3 h-3" /> Đã kết nối — {status.retailer}</span>
              ) : (
                <span className="text-red-400 flex items-center gap-1"><WifiOff className="w-3 h-3" /> Chưa kết nối</span>
              )}
              {status?.last_synced && <span className="text-slate-600">• Sync: {status.last_synced.slice(0, 16)}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={syncAll} disabled={syncing} className="bg-[#0162e8] hover:bg-[#0150c0] text-white gap-1.5 text-xs h-9">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {syncing ? "Đang sync..." : "Sync tất cả"}
          </Button>
          <Button onClick={handleClearSync} disabled={clearing} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 gap-1.5 text-xs h-9" data-testid="clear-kv-sync-btn">
            {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Xoá dữ liệu đã sync
          </Button>
        </div>
      </div>

      {/* Sync stats */}
      {status?.counts && (
        <div className="flex gap-2 flex-wrap text-[10px]">
          {Object.entries(status.counts).map(([k, v]) => (
            <span key={k} className="bg-[#1e293b] border border-[#334155] rounded px-2 py-0.5 text-slate-400">
              {k}: <span className="text-slate-200 font-medium">{typeof v === 'number' ? v : v}</span>
            </span>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-px">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} data-testid={`kv-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${subTab === t.key ? "bg-[#1e293b] text-[#4680ff] shadow" : "text-slate-500 hover:text-slate-300"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {subTab === "dashboard" && <DashboardTab />}
      {subTab === "auto_crawl" && <AutoCrawlTab />}
      {subTab === "webhooks" && <WebhooksTab />}
    </div>
  );
}

// ==================== DASHBOARD ====================
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    setLoading(true);
    api.get(`/kiotviet/dashboard?days=${days}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#4680ff] animate-spin" /></div>;
  if (!data) return <p className="text-slate-500 text-center py-12">Chưa có dữ liệu. Bấm "Sync tất cả" để bắt đầu.</p>;

  return (
    <div className="space-y-4" data-testid="kv-dashboard">
      <div className="flex gap-1">
        {[7, 14, 30].map(d => (
          <button key={d} onClick={() => setDays(d)} className={`px-2.5 py-1 rounded text-[10px] ${days === d ? "bg-[#4680ff]/20 text-[#4680ff]" : "text-slate-500"}`}>{d} ngày</button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "DOANH THU", value: data.total_revenue, gradient: "from-[#4680ff] to-[#5b8def]", spark: data.chart_data?.map(d => d.revenue) || [] },
          { label: "HOA DON", value: data.total_invoices, gradient: "from-[#05c3a6] to-[#22d6b4]", spark: data.chart_data?.map(d => d.count) || [] },
          { label: "SAN PHAM", value: data.product_count, gradient: "from-[#fd7e14] to-[#ff9a44]", spark: [] },
          { label: "KHACH HANG", value: data.customer_count, gradient: "from-[#7c3aed] to-[#a78bfa]", spark: [] },
        ].map((m, i) => (
          <div key={i} className={`relative overflow-hidden rounded-xl bg-gradient-to-b ${m.gradient} p-4 min-h-[130px] flex flex-col justify-between shadow-lg`}>
            <p className="text-[10px] font-bold text-white/80 tracking-wider uppercase">{m.label}</p>
            <p className="text-2xl font-bold text-white">{i === 0 ? fmtVND(m.value) : m.value}</p>
            <p className="text-[10px] text-white/60">{days} ngày gần nhất</p>
            {ApexChart && m.spark.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 opacity-35">
                <ApexChart type="area" height={45} width="100%" options={{
                  chart: { sparkline: { enabled: true }, animations: { enabled: true, speed: 1000 } },
                  stroke: { curve: 'smooth', width: 2 }, fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0 } },
                  colors: ['#fff'], tooltip: { enabled: false },
                }} series={[{ data: m.spark }]} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      {ApexChart && data.chart_data?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2">Doanh thu KiotViet theo ngày</p>
          <ApexChart type="area" height={200} options={{
            chart: { toolbar: { show: false }, animations: { enabled: true, speed: 800 } },
            stroke: { curve: 'smooth', width: 2.5 },
            fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 } },
            colors: ['#4680ff', '#05c3a6'],
            xaxis: { categories: data.chart_data.map(d => d.date?.slice(5) || ""), labels: { style: { fontSize: '9px' } } },
            yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '9px' } } },
            grid: { borderColor: '#334155', strokeDashArray: 3 },
            legend: { position: 'bottom', fontSize: '10px' },
            tooltip: { y: { formatter: v => fmtFull(v) } },
            dataLabels: { enabled: false },
          }} series={[
            { name: 'Doanh thu', data: data.chart_data.map(d => d.revenue) },
          ]} />
        </div>
      )}

      {/* Bottom row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* By branch */}
        {ApexChart && data.by_branch?.length > 0 && (
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">Doanh thu theo chi nhánh</p>
            <ApexChart type="donut" height={180} options={{
              labels: data.by_branch.map(b => b.branch), colors: ['#4680ff', '#05c3a6', '#fc5286', '#fd7e14'],
              legend: { position: 'bottom', fontSize: '9px', labels: { colors: '#94a3b8' } },
              tooltip: { y: { formatter: v => fmtFull(v) } }, dataLabels: { enabled: false },
            }} series={data.by_branch.map(b => b.revenue)} />
          </div>
        )}

        {/* Top products */}
        {data.top_products?.length > 0 && (
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">Top sản phẩm bán chạy</p>
            <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
              {data.top_products.slice(0, 8).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-[#334155]/30 last:border-0">
                  <span className="text-[10px] text-slate-300 truncate flex-1">{i + 1}. {p.name}</span>
                  <span className="text-[10px] text-emerald-400 font-medium ml-2">{fmtFull(p.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment methods */}
        {ApexChart && data.payment_methods?.length > 0 && (
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">Phương thức thanh toán</p>
            <ApexChart type="donut" height={180} options={{
              labels: data.payment_methods.map(p => p.method || "Khác"), colors: ['#4680ff', '#05c3a6', '#fc5286', '#fd7e14', '#7c3aed'],
              legend: { position: 'bottom', fontSize: '9px', labels: { colors: '#94a3b8' } },
              tooltip: { y: { formatter: v => fmtFull(v) } }, dataLabels: { enabled: false },
            }} series={data.payment_methods.map(p => p.amount)} />
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== WEBHOOKS ====================
function WebhooksTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const fetchStatus = () => {
    setLoading(true);
    api.get("/kiotviet/webhook/status").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchStatus(); }, []);

  const register = async () => {
    setRegistering(true);
    try { const { data } = await api.post("/kiotviet/webhook/register"); toast.success(data.message); fetchStatus(); }
    catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setRegistering(false); }
  };

  const unregister = async () => {
    if (!window.confirm("Hủy tất cả webhooks?")) return;
    try { const { data } = await api.delete("/kiotviet/webhook/unregister"); toast.success(data.message); fetchStatus(); }
    catch { toast.error("Lỗi"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#4680ff] animate-spin" /></div>;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="kv-webhooks">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2"><Wifi className="w-4 h-4 text-[#4680ff]" /> Webhook Real-time</h2>
        <div className="flex gap-2">
          {data?.total_registered > 0 && <Button onClick={unregister} variant="outline" className="border-red-500/30 text-red-400 h-8 text-xs gap-1"><Trash2 className="w-3.5 h-3.5" /> Hủy tất cả</Button>}
          <Button onClick={register} disabled={registering} className="bg-[#0162e8] hover:bg-[#0150c0] text-white gap-1.5 text-xs h-8">
            {registering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {registering ? "Đang đăng ký..." : "Đăng ký Webhooks"}
          </Button>
        </div>
      </div>

      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
        <p className="text-[10px] text-slate-500 mb-2">KiotViet FNB hỗ trợ: customer.update, customer.delete, product.update, product.delete, stock.update</p>
        <p className="text-[10px] text-amber-400 mb-3">Lưu ý: KiotViet FNB chưa hỗ trợ webhook cho hóa đơn — hóa đơn vẫn sync qua cron 30 phút.</p>

        {data?.webhooks?.length > 0 ? (
          <div className="space-y-1.5">
            {data.webhooks.map((w, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#334155]/30 last:border-0">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${w.is_active ? "bg-emerald-400" : "bg-red-400"}`} />
                  <span className="text-xs text-slate-300 font-mono">{w.type}</span>
                </div>
                <div className="flex items-center gap-3 text-[9px]">
                  <span className="text-slate-600 truncate max-w-[200px]">{w.url}</span>
                  <span className="text-slate-600">{w.registered_at?.slice(0, 16)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4 text-sm">Chưa đăng ký webhook nào. Bấm "Đăng ký Webhooks".</p>
        )}
      </div>

      {/* Recent events */}
      {data?.recent_events?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2">Events gần đây</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.recent_events.map((e, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-[#334155]/30 last:border-0">
                <div className="flex items-center gap-2">
                  <Zap className="w-3 h-3 text-[#4680ff]" />
                  <span className="text-[10px] text-slate-300 font-mono">{e.event_type}</span>
                  <Badge className="text-[7px] bg-[#4680ff]/20 text-[#4680ff] border-0">{e.data_count}</Badge>
                </div>
                <span className="text-[9px] text-slate-600">{e.received_at?.slice(11, 19)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
